import React, { useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faArrowDown, faCheck, faCloudArrowDown, faCloudCheck, faCloudSlash, faHand, faLock, faPen, faUnlock, faImageSlash,
} from "@fortawesome/pro-regular-svg-icons";
import { faCalendarClock, faCalendarDays, faChartSimple, faChartLineDown, faChartLineUp, faDrawPolygon } from "@fortawesome/pro-light-svg-icons";
import { faXmark } from "@fortawesome/pro-solid-svg-icons";
import { Link } from "react-router-dom";

import ENDPOINTS from "@/services/endpoints.ts";
import PDF_ENDPOINTS from "@/services/endpoints/pdf-endpoints.ts";

import useAuthenticationContext from "@/services/context/authentication/use-authentication-context.ts";
import useProjectDetails from "@/hooks/use-project-details.ts";
import usePromoterProjectDetails from "@/hooks/use-default-promoter-project-details.ts";
import useProjectPacksDetails from "@/hooks/use-project-packs-details.ts";

import BaamerProject from "@/models/classes/baamer-project.ts";
import SyncProvider from "@/models/enums/sync-provider.ts";
import ProjectLockStatus from "@/models/enums/project-lock-status.ts";
import FileType from "@/models/enums/file-type.ts";
import ValidationState from "@/models/enums/validation-state.ts";
import { PackDto } from "@/models/interfaces/dtos/pack-dto.ts";

import Card from "@/components/card";
import { Case, Switch } from "@/components/switch";
import ImageLoader from "@/components/images/image-loader";
import mergeClasses from "@/utils/merge-classes.ts";
import Tooltip from "@/components/tooltip";

const ProjectCard: React.FC<BaamerProject> = (project) => {
    const { authenticationResponse, hasLicencePermission } = useAuthenticationContext();

    const isSyncDisabled = project.projectSynchronisation == null || (project.projectSynchronisation?.provider !== SyncProvider.BIMSYNC);
    const isUpToDate = project.projectSynchronisation?.isUpToDate === true;

    const projectDetails = useProjectDetails(project.baamerRevisionId);
    const [projectLockStatus, setProjectLockStatus] = useState<ProjectLockStatus>(() => {
        if (project.projectLock) {
            if (project.projectLock.isLocked)
                if (project.projectLock.userLockId === authenticationResponse?.user.id)
                    return ProjectLockStatus.LOCK_OWNED;
                else
                    return ProjectLockStatus.LOCK;

            return ProjectLockStatus.UNLOCK;
        }
        return ProjectLockStatus.UNLOCK;
    });

    const { data: promoterData } = usePromoterProjectDetails(project.id, ["SHAB", "SHO"]);

    const { data: projectPacksData } = useProjectPacksDetails(project.id);

    const screenshotPack: PackDto | null = useMemo(() => {
        if (!projectPacksData) return null;
        const middle = Math.floor((projectPacksData.length - 1) / 2);
        return projectPacksData[middle];
    }, [projectPacksData]);

    useEffect(() => {
        let newStatus;
        if (project.projectLock) {
            if (project.projectLock.isLocked)
                if (project.projectLock.userLockId === authenticationResponse?.user.id)
                    newStatus = ProjectLockStatus.LOCK_OWNED;
                else
                    newStatus = ProjectLockStatus.LOCK;
            else
                newStatus = ProjectLockStatus.UNLOCK;

        } else {
            newStatus = ProjectLockStatus.UNLOCK;
        }

        setProjectLockStatus(newStatus);
    }, [project.projectLock, project.projectLock?.isLocked, authenticationResponse?.user.id]);

    const totalFiles = useMemo(() => projectDetails.files?.data?.filter((fr) => fr.file.fileType !== FileType.File3DFrom2D).length || 0, [projectDetails.files?.data]);

    const validations = projectDetails.packs?.data?.map((p) => p.validation);

    const pendingStates = useMemo(() => {
        if (validations == null) return 0;
        return validations.reduce((acc, validation) => {
            if (validation.validationState === ValidationState.ToValidate)
                return acc + 1;
            return acc;
        }, 0);
    }, [validations]);

    const syncIcon = useMemo(() => {
        const containerClassName = 'inline-flex w-6 h-6 items-center justify-center text-xl';

        if (isSyncDisabled) {
            return (
                <span className={`${containerClassName} relative text-gray-600`} aria-hidden='true'>
                    <span className='absolute inset-0 flex items-center justify-center'>
                        <FontAwesomeIcon icon={faCloudSlash} fixedWidth />
                    </span>
                    <span className='absolute inset-0 flex items-center justify-center'>
                        <FontAwesomeIcon icon={faArrowDown} fixedWidth className='text-base text-gray-800' />
                    </span>
                </span>
            );
        }

        if (isUpToDate) {
            return (
                <span className={containerClassName} aria-hidden='true' data-tooltip-id={`project-card-tooltip-${project.id}`} data-tooltip-content='Projet Treegram est à jour avec Catenda' >
                    <FontAwesomeIcon icon={faCloudCheck} fixedWidth className='text-green-500' />
                </span>
            );
        }

        return (
            <span className={containerClassName} aria-hidden='true' data-tooltip-id={`project-card-tooltip-${project.id}`} data-tooltip-content='Une nouvelle maquette est disponible sur Catenda' >
                <FontAwesomeIcon icon={faCloudArrowDown} fixedWidth className='text-red-500' />
            </span>
        );
    }, [isSyncDisabled, isUpToDate, project.id]);

    const isPilotageDisabled = useMemo(() => !(hasLicencePermission('VIEW_SURFACES') || hasLicencePermission('VIEW_CARBON')), [hasLicencePermission]);

    return (
        <>
            <Tooltip id={`project-card-tooltip-${project.id}`}/>
            <Card>
                <div className='flex flex-col gap-y-2 select-none'>
                    <div className='border-b border-gray-200'>
                        <div className='flex flex-row gap-x-2 items-center px-4 py-2'>
                            {syncIcon}

                            <span className='text-sm'>Catenda</span>

                            <div className='rounded-md px-2 py-0.5 ring-1 ring-indigo-300 ml-auto text-xs text-slate-500'>
                                {`${totalFiles} ${totalFiles === 1 ? 'Fichier' : 'Fichiers'}`}
                            </div>

                            <div className='rounded-md px-2 py-0.5 ring-1 ring-indigo-300 text-xs text-slate-500'>
                                {`${project.baamerRevisionNumber} ${project.baamerRevisionNumber === 1 ? 'Révision' : 'Révisions'}`}
                            </div>

                            <FontAwesomeIcon icon={faCalendarDays} className='text-slate-800'
                                             data-tooltip-id={`project-card-tooltip-${project.id}`}
                                             data-tooltip-content={`Projet créé le ${project.createdDate.toLocaleDateString()}`}/>
                        </div>
                    </div>

                    <div className='relative px-4 py-0 flex items-center'>
                        <div className='absolute left-4 flex flex-row items-center gap-x-2'>
                            <Switch value={projectLockStatus}>
                                <Case value={ProjectLockStatus.LOCK}>
                                    <FontAwesomeIcon icon={faLock} className='text-red-500' />
                                </Case>
                                <Case value={ProjectLockStatus.UNLOCK}>
                                    <FontAwesomeIcon icon={faUnlock} className='text-green-500' />
                                </Case>
                                <Case value={ProjectLockStatus.LOCK_OWNED}>
                                    <FontAwesomeIcon icon={faPen} className='text-blue-500' />
                                </Case>
                                <Case value={ProjectLockStatus.UNDEFINED}>
                                    <FontAwesomeIcon icon={faXmark} className='text-red-500' />
                                </Case>
                            </Switch>
                        </div>

                        <div className={`w-full min-w-0 ${project.name.length > 27 ? 'pl-14' : ''}`}>
                            <div className='truncate text-center font-semibold' title={project.name}>
                                {project.name}
                            </div>
                        </div>
                    </div>

                    <div className='px-4 pb-3.5 flex flex-col gap-y-4'>
                        <Link 
                            to={`${import.meta.env.VITE_BOOMER_URL}/Management/Surfaces/${project.projectBoomerId}`}
                            title={isPilotageDisabled ? 'Fonctionnalité non disponible dans votre licence' : ''}
                            onClick={(event) => {
                                if (isPilotageDisabled) {
                                    event.preventDefault();
                                }
                            }}
                            className={mergeClasses(
                            'group flex flex-col gap-x-4 w-full rounded-sm ring-1 ring-gray-200 shadow-md transition duration-200',
                            isPilotageDisabled
                                ? 'cursor-not-allowed opacity-50'
                                : 'cursor-pointer hover:bg-blue-200 hover:ring-2 hover:ring-blue-500 hover:shadow-blue-300'
                        )}>

                            <div className='flex flex-row flex-1 gap-x-2 border-b p-2 items-center bg-sky-50 group-hover:bg-blue-200 rounded-t-sm transition duration-200 group-hover:border-b-white'>
                                <FontAwesomeIcon fixedWidth={true} icon={faChartSimple} className='text-blue-500' />
                                <span className='text-sm'>REVUES</span>

                                <div className={`ml-auto rounded-md px-2 py-0.5 ring-1  ${pendingStates === 0 ? 'ring-green-300 text-green-500 bg-green-50' : 'ring-red-300 text-red-500 bg-red-50'}`}>
                                    <div className='flex flex-row items-center gap-x-2 text-xs'>
                                        <FontAwesomeIcon fixedWidth icon={pendingStates === 0 ? faCheck : faHand} />
                                        <span>{pendingStates === 0 ? 'A jour' : 'Traitement en cours'}</span>
                                    </div>
                                </div>
                            </div>

                            <div className='grid grid-cols-3 rounded-b-sm bg-sky-50 group-hover:bg-blue-200 transition duration-200 h-16'>
                                {promoterData &&
                                    promoterData.map((d, i) => (
                                        <div key={d.keyName} className={mergeClasses('flex flex-row items-center gap-x-2 p-2 ', i !== 0 ? 'border-l group-hover:border-l-white' : '')}>

                                            <Switch value={d.keyName}>
                                                <Case value='SHAB'>
                                                    <FontAwesomeIcon icon={faChartLineUp} className='text-blue-500' />
                                                </Case>
                                                <Case value='SHO'>
                                                    <FontAwesomeIcon icon={faChartLineDown} className='text-blue-500' />
                                                </Case>
                                            </Switch>

                                            <div className='flex flex-col' key={d.keyName}>
                                                <span className='text-left text-xs'>{d.keyName}</span>
                                                <span className='text-left text-sm font-semibold'>{new Intl.NumberFormat('fr-FR').format(d.value)} {d.unity}</span>
                                            </div>
                                        </div>
                                    ))
                                }

                                <div className='flex flex-row items-center gap-x-2 border-l p-2 group-hover:border-l-white'>
                                    <FontAwesomeIcon icon={faCalendarClock} className='text-blue-500' />

                                    <div className='flex flex-col'>
                                        <span className='text-left text-xs'>Révision{project.boomerRevisionNumber > 1 ? 's' : ''}</span>
                                        <span className='text-left text-sm font-semibold'>{project.boomerRevisionNumber}</span>
                                    </div>
                                </div>
                            </div>
                        </Link>

                        <Link to={`/takeoff/${project.id}?tab=measures`} className='flex'>
                            <div className='group relative h-38 flex-1 cursor-pointer overflow-hidden rounded-md ring ring-gray-200 shadow-md transition duration-200 hover:ring-2 hover:ring-blue-500 hover:shadow-blue-300'>
                                {screenshotPack ? (
                                    <ImageLoader
                                        src={ENDPOINTS.PACK_IMAGE(screenshotPack.packId, 'ScopeStoreyByTgm')}
                                        className='absolute inset-0 h-full w-full object-cover object-center rounded-md px-4 py-2'
                                        fallback={
                                            <ImageLoader
                                                src={PDF_ENDPOINTS.GET_PROJECT_PREVIEW(project.projectOrganisation.id, project.id)}
                                                className='absolute inset-0 h-full w-full object-cover object-center rounded-md px-4 py-2'
                                                fallback={
                                                    <FontAwesomeIcon icon={faImageSlash}
                                                                     className='absolute inset-0 m-auto flex h-full w-full items-center justify-center text-gray-500 text-3xl' />
                                                }
                                            />
                                        }
                                    />
                                ) : (
                                    <ImageLoader
                                        src={PDF_ENDPOINTS.GET_PROJECT_PREVIEW(project.projectOrganisation.id, project.id)}
                                        className='absolute inset-0 h-full w-full object-cover object-center rounded-md px-4 py-2'
                                        fallback={
                                            <FontAwesomeIcon icon={faImageSlash}
                                                             className='absolute inset-0 m-auto flex h-full w-full items-center justify-center text-gray-500 text-3xl' />
                                        }
                                    />
                                )}

                                <div className='absolute top-1 bg-white/85 rounded-md px-2 py-1 flex flex-row items-center gap-x-2'>
                                    <FontAwesomeIcon fixedWidth={true} icon={faDrawPolygon} className='text-blue-500' />
                                    <span className='text-sm'>MÉTRÉS</span>
                                </div>

                                <div className='absolute inset-0 bg-blue-500 opacity-0 transition-opacity duration-200 group-hover:opacity-20' />
                            </div>
                        </Link>
                    </div>
                </div>
            </Card>
        </>
    );
}

export default ProjectCard;